// Step 2: InfluencerAudience for signup wizard
// Collects audience demographics and analytics (followers, niches, engagement)

import React, { useState, useEffect, useRef } from 'react';

const InfluencerAudience = ({ data, onDataChange, onNext, user }) => {
  // Editable copy constants
  const COPY = {
    title: 'Audience & Reach',
    subtitle: 'Tell us about your audience!',
    continueButton: 'Continue'
  };

  const [formData, setFormData] = useState({
    socialLinks: {
      instagram: data?.socialLinks?.instagram || '',
      tiktok: data?.socialLinks?.tiktok || '',
      youtube: data?.socialLinks?.youtube || '',
      x: data?.socialLinks?.x || '',
      website: data?.socialLinks?.website || ''
    },
    media: {
      profilePictureUrl: data?.media?.profilePictureUrl || ''
    },
    audience: {
      totalFollowers: data?.audience?.totalFollowers || '',
      niches: data?.audience?.niches || [],
      engagementRate: data?.audience?.engagementRate || ''
    }
  });

  // Update parent component when form data changes
  // Use a ref to track if we've already sent this data to prevent infinite loops
  const lastSentDataRef = useRef(null);
  
  useEffect(() => {
    const dataString = JSON.stringify(formData);
    if (lastSentDataRef.current !== dataString) {
      lastSentDataRef.current = dataString;
      onDataChange(formData);
    }
  }, [formData, onDataChange]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      audience: {
        ...prev.audience,
        [name]: value
      }
    }));
  };

  const handleSocialLinkChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [name]: value
      }
    }));
  };

  const handleSocialLinkBlur = (e) => {
    const { name, value } = e.target;
    if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
      setFormData(prev => ({
        ...prev,
        socialLinks: {
          ...prev.socialLinks,
          [name]: `https://${value.trim()}`
        }
      }));
    } else if (value) {
      setFormData(prev => ({
        ...prev,
        socialLinks: {
          ...prev.socialLinks,
          [name]: value.trim()
        }
      }));
    }
  };


  const handleNicheChange = (niche) => {
    setFormData(prev => ({
      ...prev,
      audience: {
        ...prev.audience,
        niches: prev.audience.niches.includes(niche)
          ? prev.audience.niches.filter(item => item !== niche)
          : [...prev.audience.niches, niche]
      }
    }));
  };


  // Validation function
  const validate = async (stepData) => {
    const errors = [];
    const audience = stepData.audience || {};
    const socialLinks = stepData.socialLinks || {};
    
    if (!audience.totalFollowers || audience.totalFollowers <= 0) {
      errors.push('Total followers is required and must be greater than 0');
    }
    
    if (!audience.niches || audience.niches.length === 0) {
      errors.push('At least one niche is required');
    }
    
    if (!audience.engagementRate?.trim()) {
      errors.push('Engagement rate is required');
    }

    // Validate that at least one social media profile is provided
    const socialFields = ['instagram', 'tiktok', 'youtube', 'x', 'website'];
    const hasAtLeastOneSocial = socialFields.some(field => {
      const value = socialLinks[field];
      return value && value.trim().length > 0;
    });

    if (!hasAtLeastOneSocial) {
      errors.push('Please add at least one social media profile so brands can review your content.');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  };

  // Attach validate function to component
  InfluencerAudience.validate = validate;

  const nicheOptions = [
    'Lifestyle',
    'Fashion',
    'Health',
    'Finance',
    'Education',
    'Sports',
    'Food',
    'Technology',
    'Travel'
  ];

  return (
    <div>

        {/* Social Profiles Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Social Profiles</h3>
          <p className="text-sm text-gray-500 mb-4">Add at least one social media profile so brands can review your content.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Instagram URL */}
            <div>
              <label htmlFor="instagram" className="form-label">
                Instagram URL
              </label>
              <input
                id="instagram"
                name="instagram"
                type="url"
                value={formData.socialLinks.instagram}
                onChange={handleSocialLinkChange}
                onBlur={handleSocialLinkBlur}
                className="form-input"
                placeholder="https://instagram.com/yourusername"
              />
            </div>

            {/* TikTok URL */}
            <div>
              <label htmlFor="tiktok" className="form-label">
                TikTok URL
              </label>
              <input
                id="tiktok"
                name="tiktok"
                type="url"
                value={formData.socialLinks.tiktok}
                onChange={handleSocialLinkChange}
                onBlur={handleSocialLinkBlur}
                className="form-input"
                placeholder="https://tiktok.com/@yourusername"
              />
            </div>

            {/* YouTube URL */}
            <div>
              <label htmlFor="youtube" className="form-label">
                YouTube URL
              </label>
              <input
                id="youtube"
                name="youtube"
                type="url"
                value={formData.socialLinks.youtube}
                onChange={handleSocialLinkChange}
                onBlur={handleSocialLinkBlur}
                className="form-input"
                placeholder="https://youtube.com/@yourusername"
              />
            </div>

            {/* X (Twitter) URL */}
            <div>
              <label htmlFor="x" className="form-label">
                X (Twitter) URL
              </label>
              <input
                id="x"
                name="x"
                type="url"
                value={formData.socialLinks.x}
                onChange={handleSocialLinkChange}
                onBlur={handleSocialLinkBlur}
                className="form-input"
                placeholder="https://x.com/yourusername"
              />
            </div>

            {/* Website URL */}
            <div className="md:col-span-2">
              <label htmlFor="website" className="form-label">
                Website/Portfolio URL
              </label>
              <input
                id="website"
                name="website"
                type="url"
                value={formData.socialLinks.website}
                onChange={handleSocialLinkChange}
                onBlur={handleSocialLinkBlur}
                className="form-input"
                placeholder="https://yourwebsite.com"
              />
            </div>
          </div>
        </div>

        {/* Audience Section */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tell us about your audience!</h3>
        
        {/* Total Followers */}
        <div className="mb-6">
          <label htmlFor="totalFollowers" className="form-label">
            Total Followers *
          </label>
          <input
            id="totalFollowers"
            name="totalFollowers"
            type="number"
            min="1"
            value={formData.audience.totalFollowers}
            onChange={handleChange}
            className="form-input"
            placeholder="e.g., 50000"
          />
          <p className="mt-1 text-xs text-gray-500">
            Enter your total follower count across all platforms
          </p>
        </div>

        {/* Niches */}
        <div className="mb-6">
          <label className="form-label">
            Niche(s) *
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
            {nicheOptions.map((niche) => (
              <label
                key={niche}
                className={`flex items-center p-3 border rounded cursor-pointer transition-colors ${
                  formData.audience.niches.includes(niche)
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={formData.audience.niches.includes(niche)}
                  onChange={() => handleNicheChange(niche)}
                  className="sr-only"
                />
                <span className="text-sm font-medium">{niche}</span>
              </label>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Select all niches that apply to your content
          </p>
        </div>

        {/* Engagement Rate */}
        <div className="mb-6">
          <label htmlFor="engagementRate" className="form-label">
            Engagement Rate *
          </label>
          <input
            id="engagementRate"
            name="engagementRate"
            type="text"
            value={formData.audience.engagementRate}
            onChange={handleChange}
            className="form-input"
            placeholder="e.g., 4.2%"
          />
          <p className="mt-1 text-xs text-gray-500">
            Your average engagement rate (likes, comments, shares)
          </p>
        </div>

        </div>
    </div>
  );
};

export default InfluencerAudience;
